import { LoginEventPayload, OiamEvents, SendTokenPayload, SetConfigEventPayload } from '@oiambk/oiam-oauth-types';
import { EventActionDispatcher } from './event-action-dispatcher';
import { compareObjectAnyOrder } from './utils';

/**
 * Queue for all external events the oiam-oauth-wc does handle
 * Buffers incoming events until the first oiamSetConfigEvent is received.
 * Handles this event first before the buffered events get handled in order of their arrival.
 */
export class EventQueue {
  private currentConfig: SetConfigEventPayload;
  private queuedEvents: QueuedEvent[];
  private eventCurrentlyProcessing: QueuedEvent;

  constructor(private eventActionDispatcher: EventActionDispatcher) {
    this.queuedEvents = [];
  }

  async push(event: CustomEvent) {
    const eventToQueue = new QueuedEvent(event);
    // first oiamSetConfigEvent
    if (event.type === OiamEvents.SETCONFIG && !this.currentConfig) {
      await this.setEventToProcess(eventToQueue);
    } else {
      // ignore if event already in queue
      if (this.isAlreadyIn(eventToQueue)) {
        return;
      }
      this.queuedEvents.push(eventToQueue);
      // execute immediately if initial configured and not currently processing
      if (this.currentConfig && !this.eventCurrentlyProcessing) {
        await this.setEventToProcess(this.queuedEvents.shift());
        return;
      }
    }
  }

  private async setEventToProcess(event: QueuedEvent | undefined) {
    this.eventCurrentlyProcessing = event;
    // no more queuedEvents -> end
    if (!event) {
      return;
    }

    // JS objects can be sent via events and give both sender and receiver a reference to
    // the same object and both can modify it with the other side seeing the changes immediately.
    // Semantic of an event is a snapshot, not changeable retroactively.
    // Therefore clone the payload.
    const clonedPayload = event.payload && JSON.parse(JSON.stringify(event.payload));

    // special case oiamSetConfigEvent: we have to remember current config
    if (event.name === OiamEvents.SETCONFIG) {
      this.currentConfig = clonedPayload as SetConfigEventPayload;
    }
    await this.eventActionDispatcher.dispatchEvent(event.name, clonedPayload);
    // recursive call with next item in queue or undefined if queue is empty
    await this.setEventToProcess(this.queuedEvents.shift());
  }

  /**
   * Check if a given event is either
   * - the same that currently is processed
   * or
   * - it is a setConfigEvent with same config currently set
   * or
   * - it is already in the event queue waiting to be processed
   */
  private isAlreadyIn(eventToQueue: QueuedEvent): boolean {
    // first check against currently processed event
    const current = this.eventCurrentlyProcessing;
    if (current && current.name === eventToQueue.name) {
      if (compareObjectAnyOrder(eventToQueue.payload, current.payload)) {
        return true;
      }
    }
    // special case oiamSetConfigEvent: check against current set config
    if (
      eventToQueue.name === OiamEvents.SETCONFIG &&
      this.currentConfig &&
      compareObjectAnyOrder(eventToQueue.payload, this.currentConfig)
    ) {
      return true;
    }
    // check queuedEvents
    let retval = false;
    this.queuedEvents
      .filter((qE: QueuedEvent) => qE.name === eventToQueue.name)
      .forEach((qE: QueuedEvent) => {
        if (compareObjectAnyOrder(qE.payload, eventToQueue.payload)) {
          retval = true;
        }
      });
    return retval;
  }
}

class QueuedEvent {
  name: string;
  payload: SetConfigEventPayload | LoginEventPayload | SendTokenPayload | undefined;

  constructor(event: CustomEvent) {
    this.name = event.type;
    this.payload = event.detail;
  }
}
